import React, { useEffect, useState, useRef } from "react";
import person1 from "../../Assets/Images/person1.png";
import thumb from "../../Assets/Images/thumb.png";

const Testimonial = () => {
  const [current, setCurrent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentTranslate, setCurrentTranslate] = useState(0);
  const [prevTranslate, setPrevTranslate] = useState(0);
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);

  const testimonials = [
    {
      quote:
        "“Learning together inspires growth, builds confidence, and turns knowledge into lasting success",
      author: "Ray Cooper",
      role: "Founder of colab courses, lecturer",
      image: thumb,
      bgImage: person1,
    },
    {
      quote: "Collaboration is where learning becomes transformation.",
      author: "Sarah Johnson",
      role: "Design Mentor, EduPro",
      image: thumb,
      bgImage: person1,
    },
    {
      quote: "When minds connect, knowledge grows beyond boundaries.",
      author: "David Smith",
      role: "Instructor, SkillEdge",
      image: thumb,
      bgImage: person1,
    },
  ];

  // Touch and mouse event handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(getClientX(e));
    setPrevTranslate(currentTranslate);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const currentX = getClientX(e);
    const diff = currentX - startX;
    setCurrentTranslate(prevTranslate + diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const movedBy = currentTranslate - prevTranslate;
    const carouselWidth = carouselRef.current?.offsetWidth || 0;

    // Change slide if dragged more than 50px
    if (Math.abs(movedBy) > 50) {
      if (movedBy > 0) {
        // Dragged right - go to previous slide
        setCurrent((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
      } else {
        // Dragged left - go to next slide
        setCurrent((prev) => (prev + 1) % testimonials.length);
      }
    }

    setCurrentTranslate(-current * (carouselWidth || 0));
  };

  const getClientX = (e) => {
    return e.type.includes("mouse") ? e.clientX : e.touches[0].clientX;
  };

  // Update translate when current changes
  useEffect(() => {
    const carouselWidth = carouselRef.current?.offsetWidth || 0;
    setCurrentTranslate(-current * carouselWidth);
    setPrevTranslate(-current * carouselWidth);
  }, [current]);

  // auto slide - pause when dragging
  useEffect(() => {
    if (isDragging) return;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length, isDragging]);

  // trigger cursor drag mode only over this section
  useEffect(() => {
    const section = sectionRef.current;

    const handleEnter = () =>
      window.dispatchEvent(new Event("enter-drag-mode"));
    const handleLeave = () =>
      window.dispatchEvent(new Event("leave-drag-mode"));

    section.addEventListener("mouseenter", handleEnter);
    section.addEventListener("mouseleave", handleLeave);

    return () => {
      section.removeEventListener("mouseenter", handleEnter);
      section.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  // Add event listeners for mouse events
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleMouseDown = (e) => handleTouchStart(e);
    const handleMouseMove = (e) => handleTouchMove(e);
    const handleMouseUp = () => handleTouchEnd();
    const handleMouseLeave = () => {
      if (isDragging) handleTouchEnd();
    };

    carousel.addEventListener("mousedown", handleMouseDown);
    carousel.addEventListener("mousemove", handleMouseMove);
    carousel.addEventListener("mouseup", handleMouseUp);
    carousel.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      carousel.removeEventListener("mousedown", handleMouseDown);
      carousel.removeEventListener("mousemove", handleMouseMove);
      carousel.removeEventListener("mouseup", handleMouseUp);
      carousel.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [isDragging, startX, currentTranslate, prevTranslate]);

  return (
    <section
      ref={sectionRef}
      className="-mt-14 xl:-pl-15 flex flex-col md:flex-row items-center justify-between max-w-full mx-auto lg:mx-8 px-6 py-20 gap-10 overflow-hidden"
    >
      {/* Left text content */}
      <div className="md:w-1/2">
        <span className="text-primaryColor text-6xl font-bold">"</span>
        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-1 xl:mt-4 leading-snug transition-all duration-700 heading">
          {testimonials[current].quote}
        </h2>
        <p className="text-gray-600 mt-4 sub-heading">
          When minds connect, knowledge grows beyond boundaries.
        </p>

        <div className="flex items-center mt-6 gap-4">
          <img
            src={testimonials[current].image}
            alt={testimonials[current].author}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <p className="font-semibold text-gray-900">
              {testimonials[current].author}
            </p>
            <p className="text-gray-500 text-sm">
              {testimonials[current].role}
            </p>
          </div>
        </div>
      </div>

      <div
        ref={carouselRef}
        className="md:w-1/2 relative overflow-hidden rounded-2xl shadow-lg h-96 md:h-[500px] cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(${currentTranslate}px)`,
            transition: isDragging ? "none" : "transform 0.3s ease-out",
          }}
        >
          {testimonials.map((item, index) => (
            <div key={index} className="w-full flex-shrink-0 bg-primaryColor">
              <img
                src={item.bgImage}
                alt={item.author}
                className="w-full h-96 md:h-[500px] object-cover select-none"
                draggable="false"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
